/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { UNIDADE_LABELS } from './constants';

const CHART_HEIGHT = 180;

const buildDayLabels = (count) => Array.from({ length: count }, (_, index) => {
  const daysAgo = count - 1 - index;
  if (daysAgo === 0) return 'Hoje';
  if (daysAgo === 1) return 'Ontem';
  return moment().subtract(daysAgo, 'days').format('DD/MM');
});

const computeYMax = (values, meta) => {
  const peak = Math.max(...values, 1);
  const withMeta = Math.max(peak * 1.15, meta ?? 0);
  if (withMeta <= 40) return Math.ceil(withMeta / 5) * 5;
  if (withMeta <= 100) return Math.ceil(withMeta / 10) * 10;
  if (withMeta <= 200) return Math.ceil(withMeta / 20) * 20;
  return Math.ceil(withMeta / 50) * 50;
};

const hasDigitacao = (serie) =>
  Array.isArray(serie)
  && serie.length > 0
  && serie.some((v) => Number(v) > 0);

const RitmoTrendChart = ({ ultimos7Dias, metaDiaria, unidade }) => {
  const theme = useTheme();
  const unitLabel = UNIDADE_LABELS[unidade] ?? 'serviços';
  const labels = useMemo(() => buildDayLabels(ultimos7Dias.length || 7), [ultimos7Dias.length]);
  const yMax = useMemo(
    () => computeYMax(ultimos7Dias, metaDiaria),
    [ultimos7Dias, metaDiaria],
  );

  if (!hasDigitacao(ultimos7Dias)) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: CHART_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          py: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Sem digitação nos últimos 7 dias
        </Typography>
        {metaDiaria != null && (
          <Typography variant="caption" color="text.disabled" textAlign="center" sx={{ mt: 0.5 }}>
            Meta de referência: {metaDiaria}/{unitLabel} por dia útil
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <LineChart
        height={CHART_HEIGHT}
        margin={{ left: 8, right: 8, top: 8, bottom: 24 }}
        grid={{ horizontal: true }}
        xAxis={[{
          scaleType: 'point',
          data: labels,
          tickLabelStyle: { fontSize: 11 },
        }]}
        yAxis={[{
          min: 0,
          max: yMax,
          width: 32,
          tickLabelStyle: { fontSize: 11 },
        }]}
        series={[{
          data: ultimos7Dias,
          label: unitLabel,
          showMark: true,
          curve: 'monotoneX',
          valueFormatter: (value) => `${value} ${unitLabel}`,
        }]}
        slotProps={{
          legend: { sx: { display: 'none' } },
        }}
        sx={{ width: '100%' }}
      >
        {metaDiaria != null && (
          <ChartsReferenceLine
            y={metaDiaria}
            lineStyle={{
              strokeDasharray: '4 4',
              stroke: theme.palette.warning.main,
              strokeWidth: 1.5,
            }}
          />
        )}
      </LineChart>
    </Box>
  );
};

RitmoTrendChart.propTypes = {
  ultimos7Dias: PropTypes.arrayOf(PropTypes.number).isRequired,
  metaDiaria: PropTypes.number,
  unidade: PropTypes.oneOf(['servicos', 'postes']),
};

RitmoTrendChart.defaultProps = {
  metaDiaria: null,
  unidade: 'servicos',
};

export default RitmoTrendChart;
